import { Mesh, Raycaster, Vector2, Vector3 } from 'three';
import type { Runtime } from './runtime';
import type { WorldInit } from './initializeWorld';
import type { InputFrame } from '../controls/types';
import type { WhisperTarget } from '../ui/chatPanel';
import { getCanvas } from '../ui/dom';
import { createPlayerContextMenu } from '../ui/playerContextMenu';
import { DRAG_CLICK_THRESHOLD_PX } from '../controls/dragLook';
import { restoreWalkNet, type WalkNet } from '../world/life/crowd/walkNet';
import { VisitorFollow } from '../multiplayer/follow';
import { createPlayerSelection, pickVisitor } from '../multiplayer/playerSelection';

export function createPlayerInteractions(
  init: WorldInit,
  runtime: () => Runtime,
  changed: (open: boolean) => void,
  whisper: (peer: WhisperTarget) => void
) {
  const canvas = getCanvas();
  const selection = createPlayerSelection(init.pipeline.annotations);
  const raycaster = new Raycaster();
  const pointer = new Vector2();
  let selected: string | null = null;
  let net: WalkNet | null = null;
  let press: { id: number; x: number; y: number; moved: number } | null = null;
  const follow = new VisitorFollow(init.env.collision, () => {
    const baked = init.world.lifePlan.crowd?.net;
    if (!net && baked) net = restoreWalkNet(baked);
    return net;
  });
  const menu = createPlayerContextMenu(changed, {
    whisper,
    follow: (peer) => {
      follow.start(peer.id);
    },
    muted: (id) => runtime().network?.muted(id) ?? false,
    mute: (id, muted) =>
      runtime().network?.mute(id, muted) ??
      Promise.reject(new Error('The shared city is not connected.')),
  });
  const peers = () => runtime().network?.speakers() ?? [];
  const blocked = (): boolean => {
    const rt = runtime();
    return (
      !rt.network ||
      rt.menuOpen ||
      rt.ui.helpOpen ||
      rt.ui.overlay === 'visible' ||
      rt.communications.isOpen
    );
  };
  const pointAt = (x: number, y: number) => {
    const rect = canvas.getBoundingClientRect();
    if (document.pointerLockElement === canvas) {
      x = rect.left + rect.width / 2;
      y = rect.top + rect.height / 2;
    }
    pointer.set(((x - rect.left) / rect.width) * 2 - 1, (-(y - rect.top) / rect.height) * 2 + 1);
    raycaster.setFromCamera(pointer, init.camera);
    const hit = pickVisitor(raycaster.ray, peers());
    if (!hit) return null;
    raycaster.far = hit.distance - 0.1;
    const obscured = raycaster.intersectObjects(init.world.root.children, true).some((entry) => {
      if (!(entry.object instanceof Mesh)) return false;
      const materials = Array.isArray(entry.object.material)
        ? entry.object.material
        : [entry.object.material];
      return entry.object.visible && materials.some((material) => !material.transparent);
    });
    raycaster.far = Infinity;
    return obscured ? null : hit.peer;
  };
  const down = (event: PointerEvent): void => {
    if (blocked() || (event.button !== 0 && event.button !== 2)) return;
    press = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: 0 };
  };
  const move = (event: PointerEvent): void => {
    if (!press || press.id !== event.pointerId) return;
    press.moved += Math.hypot(event.movementX, event.movementY);
  };
  const up = (event: PointerEvent): void => {
    const start = press;
    press = null;
    if (!start || start.id !== event.pointerId || blocked()) return;
    if (
      Math.max(start.moved, Math.hypot(event.clientX - start.x, event.clientY - start.y)) >=
      DRAG_CLICK_THRESHOLD_PX
    )
      return;
    const peer = pointAt(event.clientX, event.clientY);
    if (!peer) {
      if (event.button === 0) {
        selected = null;
        follow.stop();
      }
      return;
    }
    event.preventDefault();
    if (selected !== peer.id) follow.stop();
    selected = peer.id;
    if (event.button === 2) menu.show(peer, event.clientX, event.clientY);
  };
  const cancel = (): void => {
    press = null;
  };
  canvas.addEventListener('pointerdown', down, true);
  canvas.addEventListener('pointermove', move, true);
  canvas.addEventListener('pointerup', up, true);
  canvas.addEventListener('pointercancel', cancel, true);
  return {
    get isOpen(): boolean {
      return menu.isOpen;
    },
    input(frame: InputFrame, dt: number): InputFrame {
      const rt = runtime();
      if (blocked() || menu.isOpen) return frame;
      if (rt.tours.state()) follow.stop();
      return follow.input(rt.control.controlState, frame, peers(), dt, performance.now());
    },
    stopFollowing(): void {
      follow.stop();
    },
    update(): void {
      const rt = runtime();
      const peer = peers().find((visitor) => visitor.id === selected) ?? null;
      if (!peer) {
        selected = null;
        follow.stop();
        menu.hide();
      }
      if (rt.menuOpen || rt.ui.helpOpen || rt.ui.overlay === 'visible' || !rt.network) menu.hide();
      selection.update(rt.menuOpen || !rt.network ? null : peer);
    },
    diagnostics() {
      const rect = canvas.getBoundingClientRect();
      return {
        selected,
        following: follow.target,
        players: peers().map((peer) => {
          const projected = new Vector3(peer.x, peer.y - 0.6, peer.z).project(init.camera);
          return {
            id: peer.id,
            name: peer.name,
            position: [peer.x, peer.y, peer.z],
            screen: [
              rect.left + ((projected.x + 1) * rect.width) / 2,
              rect.top + ((1 - projected.y) * rect.height) / 2,
            ],
          };
        }),
      };
    },
    dispose(): void {
      canvas.removeEventListener('pointerdown', down, true);
      canvas.removeEventListener('pointermove', move, true);
      canvas.removeEventListener('pointerup', up, true);
      canvas.removeEventListener('pointercancel', cancel, true);
      menu.dispose();
      selection.dispose();
      follow.stop();
    },
  };
}
